import { useCallback, useEffect, useState } from 'react'

export type CurrentWeather = {
  dt: number
  sunrise: number
  sunset: number
  temp: number
  feels_like: number
  pressure: number
  humidity: number
  dew_point: number
  uvi: number
  clouds: number
  visibility: number
  wind_speed: number
  wind_deg: number
  wind_gust: number
  weather: {
    id: number
    main: string
    description: string
    icon: string
  }
}

export interface DailyWeather {
  dt: number
  sunrise: number
  sunset: number
  moonrise: number
  moonset: number
  moon_phase: number
  summary: string
  temp: {
    day: number
    min: number
    max: number
    night: number
    eve: number
    morn: number
  }
  feels_like: {
    day: number
    night: number
    eve: number
    morn: number
  }
  pressure: number
  humidity: number
  dew_point: number
  wind_speed: number
  wind_deg: number
  wind_gust: number
  weather: [
    {
      id: number
      main: string
      description: string
      icon: string
    },
  ]
  clouds: number
  pop: number
  rain: number
  uvi: number
}

export interface OneCallWeatherResponse {
  lat: number
  lon: number
  timezone: string
  timezone_offset: number
  current: CurrentWeather
  daily: DailyWeather[]
}

interface WeatherCache {
  data: OneCallWeatherResponse
  timestamp: number
}

const WEATHER_STORAGE_KEY = 'weather_data'
const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

interface WeatherProps {
  locationLatitude: number
  locationLongitude: number
}

export const useWeather = ({ locationLatitude, locationLongitude }: WeatherProps) => {
  const [weatherData, setWeatherData] = useState<OneCallWeatherResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check if cached data is still valid
  const isDataValid = useCallback((cached: WeatherCache | null): boolean => {
    if (!cached) return false

    const now = Date.now()
    const daysSinceCache = Math.floor((now - cached.timestamp) / CACHE_DURATION)

    // Data is valid if it was cached today
    return daysSinceCache === 0
  }, [])

  // Load data from localStorage
  const loadFromCache = useCallback((): WeatherCache | null => {
    try {
      const cached = localStorage.getItem(WEATHER_STORAGE_KEY)
      return cached ? JSON.parse(cached) : null
    } catch (error) {
      console.error('Failed to load weather data from cache:', error)
      return null
    }
  }, [])

  // Save data to localStorage
  const saveToCache = useCallback((data: OneCallWeatherResponse) => {
    try {
      const cacheData: WeatherCache = {
        data,
        timestamp: Date.now(),
      }
      localStorage.setItem(WEATHER_STORAGE_KEY, JSON.stringify(cacheData))
    } catch (error) {
      console.error('Failed to save weather data to cache:', error)
    }
  }, [])

  // Fetch weather data from API
  const fetchWeatherData = useCallback(async (): Promise<OneCallWeatherResponse> => {
    console.log('fetchWeatherData')
    // Note: In a real implementation, the API key should come from environment variables
    const API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY

    if (!API_KEY) {
      throw new Error('OpenWeather API key not configured')
    }
    const response = await fetch(
      `https://api.openweathermap.org/data/3.0/onecall?lat=${locationLatitude}&lon=${locationLongitude}&lang=en&units=metric&appid=${API_KEY}`
    )
    if (!response.ok) {
      throw new Error(`Weather API request failed: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  }, [locationLatitude, locationLongitude])

  // Load weather data (from cache or API)
  const loadWeatherData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Try to load from cache first
      const cached = loadFromCache()
      if (isDataValid(cached)) {
        setWeatherData(cached?.data ?? null)
        setIsLoading(false)
        return
      }

      // If no valid cache, fetch from API
      const data = await fetchWeatherData()
      setWeatherData(data)
      saveToCache(data)
    } catch (err) {
      console.error('Failed to load weather data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load weather data')

      // Try to use stale cache data if API fails
      const cached = loadFromCache()
      if (cached) {
        setWeatherData(cached.data)
      }
    } finally {
      setIsLoading(false)
    }
  }, [isDataValid, loadFromCache, fetchWeatherData, saveToCache])

  // Force refresh weather data
  const refreshWeatherData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const data = await fetchWeatherData()
      setWeatherData(data)
      saveToCache(data)
    } catch (err) {
      console.error('Failed to refresh weather data:', err)
      setError(err instanceof Error ? err.message : 'Failed to refresh weather data')
    } finally {
      setIsLoading(false)
    }
  }, [fetchWeatherData, saveToCache])

  // Get weather for a specific date
  const getWeatherForDate = useCallback(
    (date: Date): DailyWeather | null => {
      if (!weatherData) return null

      const targetTimestamp = Math.floor(date.getTime() / 1000)

      // Find the weather day that matches the date (allowing for timezone differences)
      const weatherDay = weatherData.daily.find((day) => {
        const dayStart = Math.floor(new Date(day.dt * 1000).setHours(0, 0, 0, 0) / 1000)
        const dayEnd = Math.floor(new Date(day.dt * 1000).setHours(23, 59, 59, 999) / 1000)
        return targetTimestamp >= dayStart && targetTimestamp <= dayEnd
      })

      return weatherDay || null
    },
    [weatherData]
  )

  // Load data on mount
  useEffect(() => {
    loadWeatherData()
  }, [loadWeatherData])

  return {
    weatherData,
    isLoading,
    error,
    refreshWeatherData,
    getWeatherForDate,
  }
}
